import { useState } from 'react'
import {
  AVAILABILITY_DAYS,
  SCHEDULE_HORIZON_DAYS,
  addDays,
  availabilityDays,
  datesBetween,
  fillWeekdays,
  isSchedulable,
  lastWindow,
  monthOf,
  repeatLastWeek,
  requestDate,
  windowLabel,
} from '@kronos/domain'
import type { DayAvailability, LocalDate, LocalMonth } from '@kronos/domain'
import type { ComeInRequestView } from '../../data/schedule'
import { MonthCalendar } from '../calendar/MonthCalendar'
import { problemOf } from '../hours/time'
import { Section } from '../primitives'
import { AvailabilityCell, describeAvailability } from './AvailabilityCell'
import { AvailabilityLegend, SelectionBar } from './AvailabilityParts'
import { DayEditor } from './DayEditor'
import { useSaveAvailability } from './shared'

/**
 * When I can come in, as a month calendar: green days I can do, hatched days
 * I am off, plain days I have not said — and the team's requests marked on
 * the days they are for. Tap a day to set it.
 *
 * **Several days at once**, because that is how a fortnight is filled in:
 * "Select days" and tap them (or drag across them, or Shift-click, with a
 * mouse), then set them all in one go. The two quick fills stay as buttons,
 * and both fill **only the days not yet set** (`fillWeekdays`,
 * `repeatLastWeek`), so pressing one after marking a day off never undoes it.
 */
export function AvailabilityEditor({
  today,
  mine,
  requests,
  timeZone,
}: {
  today: LocalDate
  mine: readonly DayAvailability[]
  requests: readonly ComeInRequestView[]
  timeZone: string
}): JSX.Element {
  const [month, setMonth] = useState<LocalMonth>(monthOf(today))
  const [selecting, setSelecting] = useState(false)
  const [selection, setSelection] = useState<ReadonlySet<LocalDate>>(new Set())
  const [anchor, setAnchor] = useState<LocalDate | null>(null)
  const [editing, setEditing] = useState<readonly LocalDate[] | null>(null)
  const [problem, setProblem] = useState('')
  const save = useSaveAvailability((e) => setProblem(problemOf(e)))

  const window = lastWindow(mine)
  const weekdays = fillWeekdays(today, AVAILABILITY_DAYS, window, mine)
  const repeat = repeatLastWeek(today, AVAILABILITY_DAYS, mine)
  const unset = availabilityDays(today, AVAILABILITY_DAYS, mine).filter((d) => d.availability === null).length
  const byDate = new Map(mine.map((a) => [a.date, a]))
  const asked = (date: LocalDate): readonly ComeInRequestView[] => requests.filter((r) => requestDate(r, timeZone) === date)
  const settable = (dates: readonly LocalDate[]): readonly LocalDate[] => dates.filter((d) => isSchedulable(d, today))

  const fill = (edits: typeof weekdays): void => {
    setProblem('')
    if (edits.length > 0) save.mutate(edits)
  }
  const select = (dates: readonly LocalDate[]): void => {
    setSelecting(true)
    setSelection((s) => new Set([...s, ...settable(dates)]))
  }
  const stopSelecting = (): void => {
    setSelecting(false)
    setSelection(new Set())
  }
  const onDayPress = (date: LocalDate, how: { shiftKey: boolean }): void => {
    if (how.shiftKey && anchor) select(datesBetween(anchor, date))
    else if (selecting) {
      setSelection((s) => {
        const next = new Set(s)
        if (!next.delete(date)) next.add(date)
        return next
      })
    } else setEditing([date])
    setAnchor(date)
  }

  return (
    <Section
      label="When you can come in"
      actions={<span className="muted avail-summary">{unset === 0 ? 'Next 2 weeks all set' : `${unset} of the next ${AVAILABILITY_DAYS} days not set`}</span>}
    >
      <div className="avail-quick">
        <button disabled={save.isPending || weekdays.length === 0} onClick={() => fill(weekdays)}>
          Weekdays, {windowLabel(window)}
        </button>
        <button disabled={save.isPending || repeat.length === 0} onClick={() => fill(repeat)}>
          Same as last week
        </button>
      </div>
      <span className="muted avail-hint">Quick fills set the next two weeks, and only the days you haven’t set.</span>
      {problem ? (
        <p className="critical" role="alert">
          {problem}
        </p>
      ) : null}

      <MonthCalendar
        label="Availability"
        month={month}
        onMonthChange={setMonth}
        today={today}
        minMonth={monthOf(today)}
        maxMonth={monthOf(addDays(today, SCHEDULE_HORIZON_DAYS))}
        selection={selecting ? selection : undefined}
        isDisabled={(d) => !isSchedulable(d.date, today)}
        dayClass={(d) => `avail--${byDate.get(d.date)?.status ?? 'unset'}`}
        renderDay={(d) => <AvailabilityCell availability={byDate.get(d.date) ?? null} requests={asked(d.date)} timeZone={timeZone} />}
        describeDay={(d) => describeAvailability(byDate.get(d.date) ?? null, asked(d.date), timeZone)}
        onDayPress={onDayPress}
        onDragSelect={select}
      />

      <SelectionBar
        selecting={selecting}
        count={selection.size}
        onStart={() => setSelecting(true)}
        onCancel={stopSelecting}
        onClear={() => setSelection(new Set())}
        onSet={() => setEditing([...selection].sort())}
      />
      <AvailabilityLegend />

      {editing ? (
        <DayEditor
          dates={editing}
          mine={mine}
          requests={editing.length === 1 ? asked(editing[0] as LocalDate) : []}
          window={window}
          today={today}
          timeZone={timeZone}
          onClose={() => setEditing(null)}
          onSaved={() => {
            if (editing.length > 1) stopSelecting()
            setEditing(null)
          }}
        />
      ) : null}
    </Section>
  )
}
